import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Select as AntdSelect,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip
} from 'antd';
import { filter, map, some } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  ASSET_CATEGORY,
  MAX_LENGTHS,
  MAX_UI_LABEL_VIDEO_CAROUSEL_ITEMS,
  MODULES,
  REGEX,
  ROUTES,
  UI_LABEL_TYPE,
  UI_LABEL_TYPES,
  UI_LABEL_TYPES_OPTIONS,
  UI_LABEL_VIDEO_SOURCE_TYPE,
  UI_LABEL_VIDEO_SOURCE_TYPE_OPTIONS,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../common/constants';
import { formValidatorRules } from '../../common/utils';
import HtmlEditor from '../../components/HtmlEditor';
import PageHeader from '../../components/PageHeader';
import useCheckPermission from '../../hooks/useCheckPermission';
import SelectAsset from '../assets/components/SelectAsset';
import { SelectVideos } from '../labels/collections/components/FormInputs';
import SelectUiLabelGroup from '../pages/component/ui-labels-group/SelectUiLabelGroup';
import { CREATE_UI_LABEL, UPDATE_UI_LABEL } from './graphql/Mutations';
import { GET_LANGUAGES, GET_UI_LABEL } from './graphql/Queries';

const initialValues = {
  type: UI_LABEL_TYPES.STRING,
  key: '',
  description: '',
  referenceImage: {
    id: '',
    url: ''
  },
  asset: null,
  videoSourceType: UI_LABEL_VIDEO_SOURCE_TYPE.ASSET,
  video: null,
  videos: [],
  languageArray: []
};

const VIDEO_CONTENT_UNIT_HELP_TEXT = {
  [UI_LABEL_TYPES.VIDEO]:
    'Selecting a video auto-generates read-only fields from it: KEY_TITLE, KEY_DURATION, KEY_IMAGE_URL, KEY_DATE_TIME, KEY_DESCRIPTION, KEY_URL — plus a KEY_AUTHOR field left blank for you to fill in manually (no reliable author source). These regenerate automatically whenever you change the selected video; your AUTHOR entry is preserved.',
  [UI_LABEL_TYPES.VIDEO_CAROUSEL]:
    'Each selected video (up to 10) generates its own set of fields, prefixed by position: KEY_ITEM_1_TITLE, KEY_ITEM_1_DURATION, ... KEY_ITEM_1_AUTHOR, then KEY_ITEM_2_..., and so on. The TITLE/DURATION/IMAGE_URL/DATE_TIME/DESCRIPTION/URL fields are read-only and pulled from the video; each AUTHOR field is blank and manually editable. These regenerate automatically whenever you change the selected videos; your AUTHOR entries are preserved.'
};

const GENERATED_FIELD_KEYS = {
  IMAGE_URL: 'IMAGE_URL',
  DATE_TIME: 'DATE_TIME',
  DURATION: 'DURATION',
  DESCRIPTION: 'DESCRIPTION',
  TITLE: 'TITLE',
  URL: 'URL'
};

const GENERATED_FIELD_SOURCE_TEXT = [
  [GENERATED_FIELD_KEYS.IMAGE_URL, "From the video's image."],
  [
    GENERATED_FIELD_KEYS.DATE_TIME,
    "From the video's display date (falls back to published/created date)."
  ],
  [GENERATED_FIELD_KEYS.DURATION, "From the linked video asset's duration."],
  [GENERATED_FIELD_KEYS.DESCRIPTION, "From the video's description."],
  [GENERATED_FIELD_KEYS.TITLE, "From the video's title."],
  [
    GENERATED_FIELD_KEYS.URL,
    "The video's public watch-page URL, built from its slug."
  ]
];

const getGeneratedFieldSourceText = (key = '') =>
  GENERATED_FIELD_SOURCE_TEXT.find(([suffix]) =>
    key?.endsWith(`_${suffix}`)
  )?.[1] ?? 'Derived automatically from the linked video.';

const AddEditUILabel = ({ history, match: { params } }) => {
  const { uiLabelId } = params;
  const isEdit = !!uiLabelId;
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [form] = Form.useForm();
  const type = Form?.useWatch('type', form);
  const videoSourceType = Form?.useWatch('videoSourceType', form);
  const [childLabels, setChildLabels] = useState([]);
  const [labelMeta, setLabelMeta] = useState({
    isAutoGenerated: false,
    parentLabelId: null
  });

  const languageArrayValue = Form?.useWatch('languageArray', form);
  const languageCodeValue = Form?.useWatch('languageCode', form);

  const { data: languagesData } = useQuery(GET_LANGUAGES, {
    fetchPolicy: 'network-only',
    onCompleted: ({ languages }) => {
      if (languages?.languages?.length > 0) {
        form?.setFieldsValue({
          languageArray: map(languages?.languages, (item) => {
            return {
              languageCode: { label: item?.name, value: item?.code },
              languageValue: ''
            };
          })
        });
      }
    }
  });

  const isAssetEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isAssetViewAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const [fetchUILabel, { loading: fetchingDetails }] = useLazyQuery(
    GET_UI_LABEL,
    {
      fetchPolicy: 'network-only'
    }
  );

  const [addUpdateUILabel, { loading }] = useMutation(
    isEdit ? UPDATE_UI_LABEL : CREATE_UI_LABEL
  );

  useEffect(() => {
    if (isEdit) {
      setChildLabels([]);
      setLabelMeta({ isAutoGenerated: false, parentLabelId: null });
      fetchUILabel({
        variables: {
          id: uiLabelId
        }
      })
        .then(({ data }) => {
          if (!data) return;
          const { uiLabelAdmin } = data;
          form.setFieldsValue({
            key: uiLabelAdmin?.key,
            type: uiLabelAdmin?.type,
            languageValue: uiLabelAdmin?.value ?? '',
            languageCode: {
              label: uiLabelAdmin?.language?.name,
              value: uiLabelAdmin?.language?.code
            },
            description: uiLabelAdmin?.description ?? '',
            referenceImage: {
              id: uiLabelAdmin?.referenceImage?.id ?? '',
              url: uiLabelAdmin?.referenceImage?.url ?? ''
            },
            videoSourceType:
              uiLabelAdmin?.videoSourceType ?? UI_LABEL_VIDEO_SOURCE_TYPE.ASSET,
            video: uiLabelAdmin?.referenceVideo
              ? {
                  id: uiLabelAdmin?.referenceVideo?.id,
                  url: uiLabelAdmin?.referenceVideo?.imageThumbnail?.url ?? '',
                  title: uiLabelAdmin?.referenceVideo?.title,
                  isLive: uiLabelAdmin?.referenceVideo?.isLive
                }
              : null,
            videos: map(uiLabelAdmin?.referenceVideos, (video) => ({
              id: video?.id,
              url: video?.imageThumbnail?.url ?? '',
              title: video?.title,
              isLive: video?.isLive
            })),
            uiLabelGroup: uiLabelAdmin?.labelGroup
              ? {
                  label: uiLabelAdmin?.labelGroup?.name,
                  value: uiLabelAdmin?.labelGroup?.id
                }
              : null
          });
          const children = uiLabelAdmin?.childLabels ?? [];
          setChildLabels(children);
          setLabelMeta({
            isAutoGenerated: !!uiLabelAdmin?.isAutoGenerated,
            parentLabelId: uiLabelAdmin?.parentLabelId ?? null
          });
        })
        .catch(() => {
          //
        });
    }
  }, [uiLabelId]);

  const hasAddedOneValue = some(
    languageArrayValue,
    (item) => item?.languageValue?.length
  );

  const isContentUnitVideoSource =
    videoSourceType === UI_LABEL_VIDEO_SOURCE_TYPE.CONTENT_UNIT;

  const assetInfo = {
    [UI_LABEL_TYPES.IMAGE]: {
      modalTitle: 'Select Image',
      categoryKey: ASSET_CATEGORY.IMAGE,
      btnText: 'Image',
      dataSelector: ({ id, url }) => ({
        id,
        url
      })
    },
    // Only the Asset-sourced VIDEO uses the raw Asset picker below; a Content-Unit
    // sourced VIDEO (or a VIDEO_CAROUSEL, which is always Content-Unit sourced) uses
    // the SelectVideos picker instead.
    ...(!isContentUnitVideoSource && {
      [UI_LABEL_TYPES.VIDEO]: {
        modalTitle: 'Select Video',
        categoryKey: ASSET_CATEGORY.VIDEO,
        btnText: 'Video',
        dataSelector: ({
          id,
          serviceImageThumbnail,
          serviceVideoThumbnail
        }) => ({
          id,
          url: serviceVideoThumbnail || serviceImageThumbnail
        })
      }
    })
  }[type];

  const handleSubmit = ({
    referenceImage,
    asset: _asset,
    video,
    videos = [],
    type: uiLabelType,
    key,
    languageArray = [],
    languageValue,
    languageCode,
    uiLabelGroup,
    videoSourceType: submittedVideoSourceType,
    ...rest
  }) => {
    if (!hasAddedOneValue && !isEdit) {
      setValidationTriggered(true);
      return;
    }
    const resolvedVideoSourceType =
      uiLabelType === UI_LABEL_TYPES.VIDEO_CAROUSEL
        ? UI_LABEL_VIDEO_SOURCE_TYPE.CONTENT_UNIT
        : submittedVideoSourceType;
    addUpdateUILabel({
      variables: {
        ...(isEdit && { id: uiLabelId }),
        data: {
          ...(!isEdit
            ? {
                type: uiLabelType,
                key,
                value: map(
                  filter(languageArray, (item) => item?.languageValue?.length),
                  (item) => {
                    return {
                      languageCode: item?.languageCode?.value,
                      value: item?.languageValue
                    };
                  }
                )
              }
            : { value: languageValue }),
          referenceImageId: referenceImage?.id || null,
          labelGroupId: uiLabelGroup?.value ?? null,
          referenceVideoId: video?.id || null,
          videos: map(videos, 'id'),
          videoSourceType: resolvedVideoSourceType,
          ...rest
        }
      }
    }).then(() => {
      history.push(ROUTES?.UI_LABELS);
    });
  };

  const handleCancel = () => {
    history.push(ROUTES?.UI_LABELS);
  };

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.UI_CONFIG_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.UI_CONFIG_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const isLockedChildLabel = labelMeta?.isAutoGenerated;

  return (
    <>
      <PageHeader menu={MODULES?.UI_CONFIGURATION} />
      <div className="page-wrapper">
        <div className="page-wrapper-body">
          {isLockedChildLabel && (
            <Alert
              className="mb-16 mt-16"
              type="info"
              showIcon
              message="This label is auto-generated from its parent's video source and can't be edited directly. Edit the video on the parent label to update it."
              action={
                labelMeta?.parentLabelId && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() =>
                      history.push(
                        `${ROUTES?.UI_LABELS}/${labelMeta?.parentLabelId}/edit`
                      )
                    }
                  >
                    Go to parent label
                  </Button>
                )
              }
            />
          )}
          <Form
            form={form}
            className="add-edit-form"
            layout="vertical"
            initialValues={initialValues}
            onFinishFailed={() => setValidationTriggered(true)}
            onFinish={handleSubmit}
            disabled={isViewOnly || fetchingDetails || isLockedChildLabel}
          >
            <Form.Item
              label="Key"
              name="key"
              required
              rules={[
                {
                  validator(rule, value) {
                    if (!value) {
                      // eslint-disable-next-line prefer-promise-reject-errors
                      return Promise?.reject('Please enter a key!');
                    }
                    if (!REGEX?.KEY_VALUE?.test(value)) {
                      // eslint-disable-next-line prefer-promise-reject-errors
                      return Promise?.reject('Should be a valid key');
                    }
                    return Promise?.resolve();
                  }
                },
                // need to set 100 as per requirement
                formValidatorRules?.maxLength(100)
              ]}
            >
              <Input disabled={isEdit} placeholder="Enter key" />
            </Form.Item>
            <Form.Item
              label="Type"
              name="type"
              rules={[
                {
                  required: true,
                  message: 'Please select type!'
                }
              ]}
            >
              <AntdSelect
                disabled={isEdit}
                options={UI_LABEL_TYPES_OPTIONS}
                placeholder="Select type"
                onChange={(value) => {
                  const isVideoType =
                    value === UI_LABEL_TYPES.VIDEO ||
                    value === UI_LABEL_TYPES.VIDEO_CAROUSEL;
                  form?.setFieldsValue({
                    languageArray: map(languageArrayValue, (item) => {
                      return {
                        ...item,
                        languageValue: ''
                      };
                    }),
                    asset: null,
                    videoSourceType:
                      value === UI_LABEL_TYPES.VIDEO_CAROUSEL
                        ? UI_LABEL_VIDEO_SOURCE_TYPE.CONTENT_UNIT
                        : UI_LABEL_VIDEO_SOURCE_TYPE.ASSET,
                    video: null,
                    videos: [],
                    ...(isVideoType && { labelType: 'UI' })
                  });
                }}
              />
            </Form.Item>
            {type === UI_LABEL_TYPES.VIDEO && (
              <Form.Item label="Video Source" name="videoSourceType">
                <AntdSelect
                  options={UI_LABEL_VIDEO_SOURCE_TYPE_OPTIONS}
                  placeholder="Select video source"
                  onChange={() => {
                    form?.setFieldsValue({
                      languageArray: map(languageArrayValue, (item) => {
                        return {
                          ...item,
                          languageValue: ''
                        };
                      }),
                      languageValue: '',
                      asset: null,
                      video: null
                    });
                  }}
                />
              </Form.Item>
            )}
            {type === UI_LABEL_TYPES.VIDEO && isContentUnitVideoSource && (
              <Form.Item
                name="video"
                label={
                  <Space size={4}>
                    <span>Select Video</span>
                    <Tooltip
                      title={VIDEO_CONTENT_UNIT_HELP_TEXT[UI_LABEL_TYPES.VIDEO]}
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                rules={
                  !isEdit
                    ? [
                        {
                          validator(rule, value) {
                            if (!value?.id) {
                              // eslint-disable-next-line prefer-promise-reject-errors
                              return Promise?.reject('Please select video!');
                            }
                            return Promise?.resolve();
                          }
                        }
                      ]
                    : []
                }
              >
                <SelectVideos
                  multiple={false}
                  disabled={isViewOnly}
                  onChange={(video) => {
                    if (video?.id) {
                      if (!isEdit) {
                        form?.setFieldsValue({
                          languageArray: map(languageArrayValue, (item) => {
                            return {
                              ...item,
                              languageValue: video?.id
                            };
                          })
                        });
                      } else {
                        form?.setFieldsValue({
                          languageValue: video?.id
                        });
                      }
                    }
                  }}
                />
              </Form.Item>
            )}
            {type === UI_LABEL_TYPES.VIDEO_CAROUSEL && (
              <Form.Item
                name="videos"
                label={
                  <Space size={4}>
                    <span>Select Videos</span>
                    <Tooltip
                      title={
                        VIDEO_CONTENT_UNIT_HELP_TEXT[
                          UI_LABEL_TYPES.VIDEO_CAROUSEL
                        ]
                      }
                    >
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                rules={[
                  {
                    validator(rule, value) {
                      if (!value?.length) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise?.reject(
                          'Please select at least one video!'
                        );
                      }
                      if (value?.length > MAX_UI_LABEL_VIDEO_CAROUSEL_ITEMS) {
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise?.reject(
                          `You can select up to ${MAX_UI_LABEL_VIDEO_CAROUSEL_ITEMS} videos!`
                        );
                      }
                      return Promise?.resolve();
                    }
                  }
                ]}
              >
                <SelectVideos
                  multiple
                  disabled={isViewOnly}
                  onChange={(selectedVideos) => {
                    const videoIds = map(selectedVideos, 'id').join(', ');
                    if (!isEdit) {
                      form?.setFieldsValue({
                        languageArray: map(languageArrayValue, (item) => {
                          return {
                            ...item,
                            languageValue: videoIds
                          };
                        })
                      });
                    } else {
                      form?.setFieldsValue({
                        languageValue: videoIds
                      });
                    }
                  }}
                />
              </Form.Item>
            )}
            {!isEdit && (
              <Form.Item
                label="Label Type"
                name="labelType"
                rules={[
                  { required: true, message: 'Please select label type!' }
                ]}
              >
                <Select
                  placeholder="Select label type"
                  options={UI_LABEL_TYPE}
                  disabled={
                    type === UI_LABEL_TYPES.VIDEO ||
                    type === UI_LABEL_TYPES.VIDEO_CAROUSEL
                  }
                />
              </Form.Item>
            )}
            <Form.Item name="uiLabelGroup" label="Label Group">
              <SelectUiLabelGroup />
            </Form.Item>
            {type !== UI_LABEL_TYPES.STRING && assetInfo && (
              <Form.Item
                name="asset"
                rules={
                  !isEdit
                    ? [
                        {
                          validator(rule, value) {
                            if (!value) {
                              // eslint-disable-next-line prefer-promise-reject-errors
                              return Promise?.reject('Please select asset!');
                            }
                            return Promise?.resolve();
                          }
                        }
                      ]
                    : []
                }
              >
                <SelectAsset
                  allowClear={false}
                  onChange={({ id }) => {
                    if (id) {
                      if (!isEdit) {
                        form?.setFieldsValue({
                          languageArray: map(languageArrayValue, (item) => {
                            return {
                              ...item,
                              languageValue: id
                            };
                          })
                        });
                      } else {
                        form?.setFieldsValue({
                          languageValue: id
                        });
                      }
                    }
                  }}
                  isAssetEditAllowed={isAssetEditAllowed}
                  isAssetViewAllowed={isAssetViewAllowed}
                  {...assetInfo}
                />
              </Form.Item>
            )}

            <Form.Item
              disabled={isViewOnly}
              label="Description"
              name="description"
              rules={[formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)]}
            >
              <Input.TextArea rows={5} placeholder="Enter description" />
            </Form.Item>
            <Form.Item label="Reference Image" name="referenceImage">
              <SelectAsset
                disabled={isViewOnly}
                modalTitle="Select Reference Image"
                categoryKey={ASSET_CATEGORY.IMAGE}
                btnText="Image"
                dataSelector={({ id, url }) => ({
                  id,
                  url
                })}
                isAssetEditAllowed={isAssetEditAllowed}
                isAssetViewAllowed={isAssetViewAllowed}
              />
            </Form.Item>
            {!isEdit ? (
              <Form.List name="languageArray">
                {(fields) => (
                  <Row gutter={[16, 0]}>
                    {fields?.map((field) => (
                      <Col
                        xs={24}
                        sm={24}
                        md={24}
                        lg={12}
                        xl={12}
                        xxl={8}
                        key={field?.key}
                      >
                        <Form.Item
                          label="Language Code"
                          hidden
                          name={[field?.name, 'languageCode']}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          required={!hasAddedOneValue}
                          label={`Value (${
                            languagesData?.languages?.languages?.[field?.key]
                              ?.name
                          })`}
                          name={[field?.name, 'languageValue']}
                          rules={
                            type === UI_LABEL_TYPES.HTML
                              ? []
                              : [
                                  formValidatorRules?.maxLength(
                                    MAX_LENGTHS.DESCRIPTION
                                  ),
                                  {
                                    validator(rule, value) {
                                      if (value?.length > 0 && !value.trim()) {
                                        return Promise?.reject(
                                          new Error('Please enter value!')
                                        );
                                      }
                                      return Promise?.resolve();
                                    }
                                  }
                                ]
                          }
                        >
                          {type === UI_LABEL_TYPES.HTML ? (
                            <HtmlEditor
                              disabled={isViewOnly}
                              className="full-html"
                            />
                          ) : (
                            <Input
                              placeholder="Enter value"
                              disabled={
                                isViewOnly || type !== UI_LABEL_TYPES.STRING
                              }
                              readOnly={type !== UI_LABEL_TYPES.STRING}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>
                )}
              </Form.List>
            ) : (
              <>
                <Form.Item label="Language Code" name="languageCode" hidden>
                  <Input />
                </Form.Item>
                <Form.Item
                  label={`Value (${languageCodeValue?.label || ''})`}
                  name="languageValue"
                  rules={
                    type === UI_LABEL_TYPES.HTML
                      ? []
                      : [
                          formValidatorRules?.maxLength(
                            MAX_LENGTHS.DESCRIPTION
                          ),
                          {
                            validator(rule, value) {
                              if (!value.trim()) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise?.reject('Please enter value!');
                              }
                              return Promise?.resolve();
                            }
                          }
                        ]
                  }
                >
                  {type === UI_LABEL_TYPES.HTML ? (
                    <HtmlEditor disabled={isViewOnly} className="full-html" />
                  ) : (
                    <Input
                      placeholder="Enter value"
                      disabled={isViewOnly || type !== UI_LABEL_TYPES.STRING}
                      readOnly={type !== UI_LABEL_TYPES.STRING}
                    />
                  )}
                </Form.Item>
              </>
            )}
            {type !== UI_LABEL_TYPES.STRING
              ? null
              : !hasAddedOneValue &&
                validationTriggered &&
                !isEdit && (
                  <span className="site-result-demo-error-icon">
                    Please enter at least one language value!
                  </span>
                )}
            {isEdit && childLabels?.length > 0 && (
              <div className="generated-fields-section mb-8">
                <h4>Generated Fields</h4>
                <Row gutter={[16, 0]}>
                  {filter(childLabels, 'isAutoGenerated')?.map((child) => (
                    <Col xs={24} sm={12} md={8} key={child?.id}>
                      <Form.Item
                        label={
                          <Space size={4}>
                            <span>{child?.key}</span>
                            <Tooltip
                              title={getGeneratedFieldSourceText(child?.key)}
                            >
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Input value={child?.value} readOnly disabled />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
            <div className="d-flex button-section mb-8">
              <Space>
                {isAddEditAllowed && !isLockedChildLabel && (
                  <Button
                    disabled={loading || fetchingDetails}
                    loading={loading}
                    type="text"
                    htmlType="submit"
                    className="text-btn mr-8"
                    size="middle"
                  >
                    Save
                  </Button>
                )}
                <Button
                  disabled={loading}
                  type="text"
                  className="text-btn2"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddEditUILabel;
